// EditorPortalShell.jsx — Shared MUI Modal wrapper for full-screen editor portals.
//
// Encapsulates the duplicated Modal + container + backdrop + Suspense + CenterLoader
// that was copy-pasted in both AnnotationEditorPortal and MapEditorPortal.
//
// Props:
//   open                — controls Modal visibility
//   onClose             — called when the backdrop is clicked (or Escape if not disabled)
//   ariaLabel           — accessible name for the modal
//   children            — the lazy-loaded editor modal (already guarded by `open` at call-site)
//   disableEscapeKeyDown — default true (editors handle Escape internally)

import React, { Suspense } from 'react';
import { Modal, CircularProgress, Box } from '@mui/material';

function getReportLayoutContainer() {
    return document.getElementById('report-layout-area') || document.body;
}

function CenterLoader() {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: '#fff',
        }}>
            <CircularProgress size={32} sx={{ color: '#fff' }} />
        </Box>
    );
}

function EditorPortalShell({ open, onClose, ariaLabel, children, disableEscapeKeyDown = true }) {
    return (
        <Modal
            open={open}
            onClose={onClose}
            disableEscapeKeyDown={disableEscapeKeyDown}
            disableEnforceFocus
            aria-label={ariaLabel}
            container={getReportLayoutContainer}
            sx={{ position: 'absolute' }}
            slotProps={{ backdrop: { sx: { position: 'absolute', bgcolor: 'rgba(0,0,0,0.5)' } } }}
        >
            <div style={{ position: 'absolute', inset: 0, outline: 'none' }}>
                <Suspense fallback={<CenterLoader />}>
                    {children}
                </Suspense>
            </div>
        </Modal>
    );
}

export default EditorPortalShell;
